import { cloneElement, MouseEvent, ReactElement } from 'react'

import { Plan } from 'context/AuthContext'
import { useCreationBlocked } from 'hooks/useCreationBlocked'
import { isGroupMode } from 'lib/merchant-group'

import { useInCompanyForm } from 'components/CompanySelector/CompanySessionProvider'

type CreateGateProps = {
  /** Botão (ou qualquer elemento clicável) que dispara a criação. */
  children: ReactElement
  /** Nome do recurso exibido no modal de upgrade. Ex: 'Cobranças'. */
  resourceName: string
  /** Plano sugerido no modal. Default: 'start'. */
  plan?: Plan
}

/**
 * Gate de criação por plano (SC-10363).
 *
 * Envolve um botão de "criar novo". Quando o merchant está bloqueado (plano Free
 * ou trial expirado), o botão continua visível e habilitado, mas o clique abre o
 * modal de upgrade em vez de executar o handler original — o usuário entende o
 * que precisa fazer, em vez de encontrar um botão sumido ou inerte.
 *
 * Fora do bloqueio, renderiza o filho intacto.
 *
 * Use apenas em ações de CRIAR. Edição, listagem, saque e exportação seguem
 * liberados para esses merchants.
 */
const CreateGate = ({
  children,
  resourceName,
  plan = 'start'
}: CreateGateProps) => {
  const { creationBlocked, openUpgradeModal } = useCreationBlocked()
  const inCompanyForm = useInCompanyForm()

  // SC-10999: na visão multi-empresas a empresa da criação só é escolhida no
  // formulário. Fora dele o bloqueio da empresa de contexto não diz nada sobre
  // a empresa que vai ser escolhida: quem barra é o gate do envio do
  // formulário, com a sessão da empresa escolhida.
  if (!creationBlocked || (isGroupMode() && !inCompanyForm)) return children

  const handleBlockedClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    openUpgradeModal(resourceName, plan)
  }

  return cloneElement(children, { onClick: handleBlockedClick })
}

export default CreateGate
